import type { InboundCallInfo } from '../../index.types';

import { PHONE_INCOMING_128, PHONE_INCOMING_128_S3 } from 'Constants/assets';
import { IS_ELECTRON } from 'Constants/env';
import { STORE_NOTIFICATION, STORE_PERSON, STORE_UI } from 'Constants/stores';
import type { ContactModelV2 } from 'Models/ContactModelV2';
import { useNotificationStore } from 'Modules/notification/store';
import {
  checkWebNotificationEligibility,
  handleDesktopNotificationClick,
} from 'Modules/notification/utils';
import { getDesktopNotificationContent } from 'Modules/notification/utils/desktopNotificationContent';
import { checkInboundPrerequisites } from 'Modules/softphone/guards';
import { EventService } from 'Modules/softphone/services/EventService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createInboundCallSession } from 'Modules/softphone/utils/callFactories';
import { extractCallInfo } from 'Modules/softphone/utils/callParsers';
import { createLogger } from 'Modules/softphone/utils/logger';
import { isPhoneNumberString } from 'Modules/softphone/utils/validation';
import type { Invitation, SessionState } from 'sip.js';
import { SessionState as SipSessionState } from 'sip.js';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';

const logger = createLogger('CallManager:InboundCallHandler');

export class InboundCallHandler {
  private activeInboundCalls: Map<string, InboundCallInfo> = new Map();
  private sessionListeners: Map<string, () => void> = new Map();
  private videoTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private rootStoreProps: RootStoreProps | null = null;

  /**
   * Set RootStoreProps instance for accessing stores
   */
  setRootStoreProps(rootStoreProps: RootStoreProps): void {
    this.rootStoreProps = rootStoreProps;
    logger.debug('setRootStoreProps called', {
      hasRootStoreProps: !!rootStoreProps,
      hasPerson: !!rootStoreProps[STORE_PERSON],
      hasNotification: !!rootStoreProps[STORE_NOTIFICATION],
    });
  }

  /**
   * Handle incoming SIP INVITE
   */
  async handleIncomingInvite(
    invitation: Invitation,
    onAudioTypeNeeded: () => 'ringtone' | 'secondIncoming',
    onPlayAudio: (audioType: 'ringtone' | 'secondIncoming') => void
  ): Promise<void> {
    const callId = invitation.id;

    logger.info('Processing incoming INVITE', { callId });

    // Check prerequisites with rootStoreProps
    // Map RootStoreProps to RootStore-like object for checkInboundPrerequisites
    const rootStoreForCheck = this.rootStoreProps
      ? {
          personStore: this.rootStoreProps[STORE_PERSON],
          uiStore: this.rootStoreProps[STORE_UI],
        }
      : undefined;

    if (!checkInboundPrerequisites(rootStoreForCheck)) {
      logger.info('Prerequisites not met, rejecting call', { callId });
      // Recorded before the reject: the timeline would otherwise show an INVITE
      // answered by a rejection with nothing saying the app chose it.
      EventService.getInstance().logCallEvent('Incoming call rejected', {
        callId,
        direction: 'inbound',
        reason: 'inbound prerequisites not met',
      });
      await invitation.reject();
      return;
    }

    // Extract call information from SIP headers
    const callInfo = await extractCallInfo(invitation);

    EventService.getInstance().logCallEvent('Incoming call', {
      callId,
      direction: 'inbound',
      phoneNumber: callInfo.phoneNumber,
      displayName: callInfo.displayName,
      isVideo: callInfo.isVideo,
    });

    // Query contact store for caller information
    // Pass SIP displayName to be used as location if no contact found
    const callerInfo = await this.queryCallerInfo(
      callInfo.phoneNumber,
      callInfo.displayName,
      callInfo.personId
    );

    // Determine audio type BEFORE adding to store
    const audioType = onAudioTypeNeeded();

    // Add to incomingCalls store
    // Use callerInfo.displayName (only if contact found)
    const tempCallSession = createInboundCallSession(
      callInfo.invitation,
      callInfo.phoneNumber,
      callerInfo.displayName,
      callInfo.personId
    );
    useSoftphoneStore.getState().addIncomingCall(tempCallSession);

    // Create notification
    // Pass raw data - let the banner/hook decide what to display
    const notificationId = this.createInboundNotification({
      ...callInfo,
      displayName: callerInfo.displayName, // Contact name (if found)
      avatarUrl: callerInfo.avatarUrl, // Contact avatar (if found)
      location: callerInfo.location, // Location from SIP header (if no contact)
      // Hook will use: displayName > location > phoneNumber for label
      // Hook will use: displayName for initials (only if it's a real name)
    });

    // Store call info
    this.activeInboundCalls.set(callId, {
      ...callInfo,
      notificationId,
    });

    // Setup session state listener
    this.setupInboundSessionListener(invitation);

    // Start video timeout if video call
    if (callInfo.isVideo) {
      this.startVideoTimeout(callId, () => {
        // Timeout expired - dismiss notification
        // Audio update will be handled by CallManager when session terminates
        this.handleInboundCallTerminated(callId, () => {
          // No-op: Audio is managed by CallManager
        });
      });
    }

    // Play audio
    onPlayAudio(audioType);

    // Trigger desktop notification if window not in foreground
    if (!document.hasFocus()) {
      this.triggerDesktopNotification(callInfo, callerInfo);
    }
  }

  /**
   * Handle inbound call termination (caller hung up or ring group member answered)
   */
  handleInboundCallTerminated(callId: string, onUpdateAudio: () => void): void {
    logger.info('Inbound call terminated', { callId });

    // Cancel video timeout if exists
    this.cancelVideoTimeout(callId);

    const callInfo = this.activeInboundCalls.get(callId);

    if (!callInfo) {
      logger.debug('Call not found in active calls', { callId });
      return;
    }

    // Dismiss notification
    if (callInfo.notificationId) {
      const notificationStore = useNotificationStore.getState();
      notificationStore.removeNotify('tr', callInfo.notificationId);
      logger.debug('Notification removed', {
        callId,
        notificationId: callInfo.notificationId,
      });
    }

    // Remove from incomingCalls store
    const softphoneStore = useSoftphoneStore.getState();

    // An answered call lives in activeCalls and its ending is recorded by the
    // session handler. Reaching here without one means the call ended while it
    // was still ringing — cancelled, picked up elsewhere in the ring group, or
    // timed out — which nothing else on the timeline would say.
    if (!softphoneStore.activeCalls.has(callId)) {
      EventService.getInstance().logCallEvent(
        'Incoming call ended before answer',
        {
          callId,
          direction: 'inbound',
          phoneNumber: callInfo.phoneNumber,
        }
      );
    }

    softphoneStore.removeIncomingCall(callId);

    // Stop audio if this was the last inbound call
    onUpdateAudio();

    // Cleanup
    this.cleanupInboundCall(callId);
    logger.debug('Cleanup complete', { callId });
  }

  /**
   * Query contact store for caller information
   * Uses the same approach as useSoftphoneContact hook:
   * 1. If internal user (personId): lookup person by ID from PersonStore
   * 2. If external call: lookup contact by phone number (awaiting API response)
   * 3. If no contact: use displayName from SIP header as location
   */
  private async queryCallerInfo(
    phoneNumber: string,
    sipDisplayName?: string,
    personId?: string
  ): Promise<{
    displayName?: string;
    avatarUrl?: string;
    location?: string;
    isInternal?: boolean;
  }> {
    try {
      if (!this.rootStoreProps) {
        logger.warn('RootStoreProps not available, skipping contact lookup');
        return { location: sipDisplayName, isInternal: false };
      }

      const personStore = this.rootStoreProps[STORE_PERSON];

      if (!personStore) {
        logger.warn('PersonStore not available in rootStoreProps');
        return { location: sipDisplayName, isInternal: false };
      }

      // Priority 1: Internal user (has personId) - same as useSoftphoneContact
      if (personId) {
        logger.debug('Internal call detected, looking up person by ID', {
          personId,
        });

        try {
          // Load person if not in cache (same as useSoftphoneContact)
          await personStore.loadPersonByIdGetIfMissingGet(personId);

          // Get person from cache (same as useSoftphoneContact)
          const person: ContactModelV2 | null =
            personStore.selectPersonValueById(Number(personId)) ?? null;

          if (person) {
            logger.debug('Found person by ID', {
              personId,
              name: person.name,
            });

            return {
              displayName: person.name || undefined,
              avatarUrl: person.avatarUrl,
              location: undefined, // Internal user, no location needed
              isInternal: true,
            };
          }
        } catch (error) {
          logger.warn('Failed to get person by ID', {
            personId,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      // Priority 2: External call - lookup contact by phone number
      // We need to await the API response, not just check synchronously
      const contact = await this.lookupContactByPhone(personStore, phoneNumber);

      if (contact) {
        logger.debug('Found contact by phone', {
          phoneNumber,
          name: contact.name,
        });

        return {
          displayName: contact.name || undefined,
          avatarUrl: contact.avatarUrl || undefined,
          location: undefined, // Contact found, no need for location
          isInternal: false,
        };
      }

      // Priority 3: No contact found - use SIP displayName as location
      // But only if it's not a phone number (SIP sometimes sends number as displayName)
      const sipDisplayNameIsPhone = isPhoneNumberString(sipDisplayName);
      const locationValue =
        sipDisplayName && !sipDisplayNameIsPhone ? sipDisplayName : undefined;

      logger.debug('No contact found', {
        phoneNumber,
        sipDisplayName,
        sipDisplayNameIsPhone,
        locationValue,
      });

      return {
        displayName: undefined,
        avatarUrl: undefined,
        location: locationValue,
        isInternal: false,
      };
    } catch (error) {
      logger.error('Failed to query contact info', {
        error: error instanceof Error ? error.message : String(error),
        phoneNumber,
        personId,
      });

      // Fallback to SIP displayName as location (if not a phone number)
      const fallbackLocation =
        sipDisplayName && !isPhoneNumberString(sipDisplayName)
          ? sipDisplayName
          : undefined;
      return { location: fallbackLocation, isInternal: false };
    }
  }

  /**
   * Lookup contact by phone number, awaiting the API response
   */
  private async lookupContactByPhone(
    personStore: RootStoreProps[typeof STORE_PERSON],
    phoneNumber: string
  ): Promise<ContactModelV2 | null> {
    try {
      // Get the IPromiseBasedObservable from PersonStore
      const contactsPbo = personStore.getAllContactsWithSameNum(phoneNumber);

      if (contactsPbo === undefined) {
        return null;
      }

      // If already fulfilled (cached), return immediately
      if (contactsPbo.state === 'fulfilled') {
        return contactsPbo.value.length > 0 ? contactsPbo.value[0] : null;
      }

      // Await the API response (IPromiseBasedObservable is "thenable")
      const contacts = await contactsPbo;

      return contacts.length > 0 ? contacts[0] : null;
    } catch (error) {
      logger.warn('Contact lookup failed', {
        phoneNumber,
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }

  /**
   * Create inbound call notification
   */
  private createInboundNotification(
    callInfo: InboundCallInfo & {
      displayName?: string;
      avatarUrl?: string;
      location?: string;
    }
  ): string | number {
    const notificationStore = useNotificationStore.getState();

    // Extract only the fields needed for InboundCallData
    const inboundCallData = {
      callId: callInfo.callId,
      phoneNumber: callInfo.phoneNumber,
      displayName: callInfo.displayName,
      avatarUrl: callInfo.avatarUrl,
      location: callInfo.location,
      ringGroupName: callInfo.ringGroupName,
      isVideo: callInfo.isVideo,
    };

    const notificationId = notificationStore.addNotify({
      priority: 'call',
      position: 'tr',
      autoDismiss: false,
      dismissible: 'none',
      inboundCallData,
    });

    logger.debug('Created notification', {
      callId: callInfo.callId,
      notificationId,
    });

    return notificationId;
  }

  /**
   * Setup session state listener for inbound call
   */
  private setupInboundSessionListener(invitation: Invitation): void {
    const callId = invitation.id;

    const stateChangeHandler = (state: SessionState) => {
      logger.debug('Invitation state changed', { callId, state });

      if (state === SipSessionState.Terminated) {
        // Will be handled by CallManager
      }
    };

    invitation.stateChange.addListener(stateChangeHandler);

    this.sessionListeners.set(callId, () => {
      invitation.stateChange.removeListener(stateChangeHandler);
    });
  }

  /**
   * Start video call timeout (60 seconds)
   * Auto-dismisses video call notification after timeout
   */
  private startVideoTimeout(callId: string, onTimeout: () => void): void {
    const timeoutId = setTimeout(() => {
      logger.info('Video call timeout expired', { callId });
      onTimeout();
    }, 60000); // 60 seconds

    this.videoTimeouts.set(callId, timeoutId);
    logger.debug('Video timeout started', { callId });
  }

  /**
   * Cancel video call timeout
   * Clears the timeout and removes it from the map
   */
  private cancelVideoTimeout(callId: string): void {
    const timeoutId = this.videoTimeouts.get(callId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.videoTimeouts.delete(callId);
      logger.debug('Video timeout cancelled', { callId });
    }
  }

  /**
   * Trigger desktop notification
   *
   * IMPORTANT: Uses silent: true to prevent browser from playing its own notification sound.
   * The application manages audio playback via AudioService to ensure consistent behavior.
   */
  private triggerDesktopNotification(
    callInfo: InboundCallInfo,
    callerInfo: {
      displayName?: string;
      location?: string;
      isInternal?: boolean;
    }
  ): void {
    // Check web notification eligibility (API, permission, user preference)
    const notificationStore = this.rootStoreProps?.[STORE_NOTIFICATION];
    const webNotificationsEnabled =
      notificationStore?.webNotificationsEnabled ?? false;

    const { canShow, reason } = checkWebNotificationEligibility(
      webNotificationsEnabled
    );

    if (!canShow) {
      logger.debug('Cannot show desktop notification', { reason });
      return;
    }

    // Format phone number for display if no name/location available
    const formattedPhone = formatNumberWithNationalCode(callInfo.phoneNumber);
    const displayName = callerInfo.displayName || callerInfo.location;

    const { title, body } = getDesktopNotificationContent(
      callInfo.isVideo
        ? { isVideo: true, displayName }
        : {
            displayName,
            phoneNumber: formattedPhone,
            ringGroupName: callInfo.ringGroupName,
            isInternal: callerInfo.isInternal,
          }
    );

    // Match legacy PhoneStore icon (bundled asset for web, S3 URL for Electron)
    const icon = callInfo.isVideo
      ? '/icons/video-call.png'
      : IS_ELECTRON
      ? PHONE_INCOMING_128_S3
      : PHONE_INCOMING_128;

    try {
      const tag = `inbound-call-${callInfo.callId}`;
      const notification = new Notification(title, {
        body,
        icon,
        tag,
        requireInteraction: true,
        silent: true, // Prevent browser from playing its own notification sound
      });

      // Restore/focus via main process — window.focus() cannot unminimize Electron windows.
      notification.onclick = () => {
        handleDesktopNotificationClick(notification, tag);
      };

      // Auto-close notification when call is terminated
      const callInfo_ = this.activeInboundCalls.get(callInfo.callId);
      if (callInfo_?.invitation) {
        const stateChangeHandler = (state: SessionState) => {
          if (state === SipSessionState.Terminated) {
            notification.close();
          }
        };
        callInfo_.invitation.stateChange.addListener(stateChangeHandler);

        // Store cleanup function to remove listener later
        const existingCleanup = this.sessionListeners.get(callInfo.callId);
        this.sessionListeners.set(callInfo.callId, () => {
          callInfo_.invitation.stateChange.removeListener(stateChangeHandler);
          if (existingCleanup) existingCleanup();
        });
      }

      logger.debug('Desktop notification created', {
        callId: callInfo.callId,
        title,
        body,
      });
    } catch (error) {
      logger.error('Failed to create desktop notification', {
        error: (error as Error).message,
        callId: callInfo.callId,
      });
    }
  }

  /**
   * Cleanup inbound call resources
   */
  private cleanupInboundCall(callId: string): void {
    // Remove from active inbound calls
    this.activeInboundCalls.delete(callId);

    // Cleanup session listener
    const removeListener = this.sessionListeners.get(callId);
    if (removeListener) {
      removeListener();
      this.sessionListeners.delete(callId);
    }

    logger.debug('Inbound call cleanup complete', { callId });
  }

  /**
   * Get active inbound call info
   */
  getCallInfo(callId: string): InboundCallInfo | undefined {
    return this.activeInboundCalls.get(callId);
  }

  /**
   * Cleanup all inbound calls
   */
  cleanup(): void {
    // Clear all video timeouts
    this.videoTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.videoTimeouts.clear();

    this.activeInboundCalls.clear();
    this.sessionListeners.forEach((removeListener) => {
      try {
        removeListener();
      } catch (e) {
        logger.warn('Error removing session listener', {
          error: (e as Error).message,
        });
      }
    });
    this.sessionListeners.clear();
  }
}
